import React from "react";
import {
  Table, TableBody, TableHead, Box, CircularProgress, Chip, Typography, TableSortLabel
} from "@mui/material";
import { CheckCircle, Block } from "@mui/icons-material";
import {
  StyledTableContainer,
  StyledTableCell,
  StyledTableRow,
} from "../../assets/styles/tableStyle";
import { Driver, getStatusColor } from "./driverTypes";
import AppVersionCell from "../../components/AppVersionCell";

// Sortable column headers, in display order. Keeps the 20-column header DRY.
const DRIVER_COLUMNS: { key: string; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'userUid', label: 'User UID' },
  { key: 'appType', label: 'AppType' },
  { key: 'currentCity', label: 'CurrentCity' },
  { key: 'phone', label: 'Phone' },
  { key: 'appVersion', label: 'App Version' },
  { key: 'licenseNumber', label: 'License Number' },
  { key: 'vehicleLicensePlateNumber', label: 'Vehicle Plate' },
  { key: 'isAvailable', label: 'Available' },
  { key: 'status', label: 'Status' },
  { key: 'rating', label: 'Rating' },
  { key: 'isDeleted', label: 'Deleted' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'updatedAt', label: 'Updated At' },
  { key: 'enrolledBy', label: 'Enrolled By' },
  { key: 'enrolledAt', label: 'Enrolled At' },
  { key: 'approvedBy', label: 'Approved By' },
  { key: 'approvedAt', label: 'Approved At' },
  { key: 'isRCVerified', label: 'RC Verified' },
  { key: 'isDLVerified', label: 'DL Verified' },
  { key: 'isProfilePhotoVerified', label: 'Profile Verified' },
];

const boolIcon = (v?: boolean) =>
  v ? <CheckCircle color="success" fontSize="small" /> : <Block color="error" fontSize="small" />;

interface Props {
  rows: Driver[];
  loading: boolean;
  order: 'asc' | 'desc';
  orderBy: string;
  onSort: (property: string) => void;
  onRowClick: (id: number) => void;
  onContextMenu: (e: React.MouseEvent, row: Driver) => void;
  /** Live LOCATION_PING_MIN_APP_VERSION, so the version chip can mark probe-eligible builds. */
  appVersionGate?: number;
}

/** The driver list table (sortable headers + row rendering). */
const DriverTable: React.FC<Props> = ({ rows, loading, order, orderBy, onSort, onRowClick, onContextMenu, appVersionGate = 0 }) => (
    <StyledTableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Table stickyHeader sx={{ minWidth: 1200 }}>
          <TableHead>
            <StyledTableRow>
              {DRIVER_COLUMNS.map((col) => (
                  <StyledTableCell key={col.key}>
                    <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => onSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {rows.map((driver) => (
                <StyledTableRow
                    key={driver.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onContextMenu={e => onContextMenu(e, driver)}
                    onClick={() => onRowClick(driver.id)}
                >
                  <StyledTableCell>{driver.id}</StyledTableCell>
                  <StyledTableCell>{driver.userUid}</StyledTableCell>
                  <StyledTableCell>{driver.user?.appType}</StyledTableCell>
                  <StyledTableCell>{driver.user?.currentCity}</StyledTableCell>
                  <StyledTableCell>{driver.user?.phone}</StyledTableCell>
                  <StyledTableCell>
                    <AppVersionCell
                        driverAppVersion={driver.user?.driverAppVersion}
                        riderAppVersion={driver.user?.riderAppVersion}
                        role="driver"
                        lastSeen={driver.user?.fcmTokenUpdatedAt}
                        gate={appVersionGate}
                    />
                  </StyledTableCell>
                  <StyledTableCell>{driver.licenseNumber || "-"}</StyledTableCell>
                  <StyledTableCell>{driver.vehicleLicensePlateNumber || "-"}</StyledTableCell>
                  <StyledTableCell>{boolIcon(driver.isAvailable)}</StyledTableCell>
                  <StyledTableCell>
                    <Chip label={driver.status || "N/A"} color={getStatusColor(driver.status)} size="small" />
                  </StyledTableCell>
                  <StyledTableCell>{driver.rating?.toFixed(1) ?? "-"}</StyledTableCell>
                  <StyledTableCell>
                    {driver.isDeleted ? <Chip label="Yes" color="error" /> : <Chip label="No" color="success" />}
                  </StyledTableCell>
                  <StyledTableCell>{driver.createdAt ? driver.createdAt.slice(0, 10) : "-"}</StyledTableCell>
                  <StyledTableCell>{driver.updatedAt ? driver.updatedAt.slice(0, 10) : "-"}</StyledTableCell>
                  <StyledTableCell>
                    {driver.enrolledBy ? `${driver.enrolledBy.firstName} ${driver.enrolledBy.lastName || ''}`.trim() : "-"}
                  </StyledTableCell>
                  <StyledTableCell>{driver.enrolledAt ? driver.enrolledAt.slice(0, 10) : "-"}</StyledTableCell>
                  <StyledTableCell>
                    {driver.approvedBy ? `${driver.approvedBy.firstName} ${driver.approvedBy.lastName || ''}`.trim() : "-"}
                  </StyledTableCell>
                  <StyledTableCell>{driver.approvedAt ? driver.approvedAt.slice(0, 10) : "-"}</StyledTableCell>
                  <StyledTableCell>{boolIcon(driver.isRCVerified)}</StyledTableCell>
                  <StyledTableCell>{boolIcon(driver.isDLVerified)}</StyledTableCell>
                  <StyledTableCell>{boolIcon(driver.isProfilePhotoVerified)}</StyledTableCell>
                </StyledTableRow>
            ))}
            {rows.length === 0 && !loading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={DRIVER_COLUMNS.length} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>No drivers found matching filters.</Typography>
                  </StyledTableCell>
                </StyledTableRow>
            )}
          </TableBody>
        </Table>
      )}
    </StyledTableContainer>
);

export default DriverTable;
